import { ArrowUpRight, Heart, MapPin, MessageCircle } from "lucide-react";
import { REGISTER_URL } from "../data/content";
import { Picture } from "./Picture";

const PROFILES = [
  {
    name: "Miso",
    breed: "Britisch Kurzhaar",
    age: "3 Jahre",
    motto: "Verfügbar für Dates",
    distance: "Hannover · 1,2 km",
    hashtags: ["#katze", "#bkh", "#familienhund"],
    image: "/images/showcase-miso.jpg",
  },
  {
    name: "Lina",
    breed: "Golden Retriever",
    age: "5 Jahre",
    motto: "Liebevoll · verspielt",
    distance: "Hamburg · 320 m",
    hashtags: ["#hund", "#goldenretriever", "#wandern"],
    image: "/images/showcase-lina.jpg",
  },
] as const;

export function Showcase(): JSX.Element {
  return (
    <section
      id="ueber-uns"
      aria-labelledby="showcase-title"
      className="bg-surface-container py-20 sm:py-24 lg:py-28"
    >
      <div className="container-welope">
        <div className="bloom-on-scroll mx-auto max-w-3xl text-center">
          <p className="section-eyebrow">Für Tier und Mensch</p>
          <h2
            id="showcase-title"
            className="mt-3 font-headline text-headline-xl text-primary sm:text-[44px]"
          >
            Profile mit Charakter.
          </h2>
          <p className="mt-5 font-body text-body-lg text-on-surface-variant">
            Jedes Welope-Profil ist eine eigene kleine Bühne — mit Platz für die schönsten
            Erinnerungen, gepflegt von allen Haltern gemeinsam.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-lg sm:grid-cols-2">
          {PROFILES.map((p, idx) => (
            <ProfileCard key={p.name} profile={p} delayMs={idx * 100} />
          ))}
        </div>

        <div className="bloom-on-scroll mt-12 rounded-xl border border-outline-variant/40 bg-surface-container-lowest p-md">
          <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="font-headline text-headline-md text-on-surface">
              Welope ist gerade im Aufbau — sei früh dabei und gestalte mit, was daraus wird.
            </p>
            <a href={REGISTER_URL} className="btn-secondary whitespace-nowrap">
              Auch dabei sein
              <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

interface Profile {
  readonly name: string;
  readonly breed: string;
  readonly age: string;
  readonly motto: string;
  readonly distance: string;
  readonly hashtags: ReadonlyArray<string>;
  readonly image: string;
}

function ProfileCard({ profile, delayMs }: { profile: Profile; delayMs: number }): JSX.Element {
  const profileId = profile.name.toLowerCase();
  return (
    <article
      className="bloom-on-scroll card-bloom overflow-hidden"
      style={{ transitionDelay: `${delayMs}ms` }}
    >
      <div className="relative aspect-[4/3] w-full overflow-hidden">
        <Picture
          src={profile.image}
          alt={`Foto von ${profile.name}, ${profile.breed}`}
          width={600}
          height={450}
          className="h-full w-full object-cover"
        />
      </div>

      <div className="space-y-sm p-md">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-headline text-headline-md text-on-surface">{profile.name}</h3>
            <p className="font-body text-label-sm text-on-surface-variant">
              {profile.breed} · {profile.age} · {profile.motto}
            </p>
          </div>
          <span className="flex items-center gap-1 font-body text-label-sm text-on-surface-variant">
            <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
            {profile.distance}
          </span>
        </div>

        <p className="font-body text-label-sm text-on-surface-variant">
          {profile.hashtags.join(" · ")}
        </p>

        <div className="flex items-center gap-5 border-t border-surface-variant/50 pt-3">
          <span className="flex items-center gap-1.5 font-body text-label-sm text-secondary">
            <Heart className="h-4 w-4 fill-secondary" aria-hidden="true" />
            248
          </span>
          <span className="flex items-center gap-1.5 font-body text-label-sm text-on-surface-variant">
            <MessageCircle className="h-4 w-4" aria-hidden="true" />
            12
          </span>
          <a
            href={`#${profileId}`}
            aria-label={`Profil von ${profile.name} ansehen`}
            className="ml-auto inline-flex items-center gap-1 font-body text-label-sm text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-sm"
          >
            Profil ansehen
            <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
          </a>
        </div>
      </div>
    </article>
  );
}
